import React, { useEffect, useRef, useState } from 'react';
import './ChangeCarTypeModal.css';
import CarSizeOptions from './CarSizeOptions';
import { normalizeCarSize } from '../utils/carSize';

// `focusField` ('plate' | 'color' | 'size') puts the cursor in the field whose row was clicked in
// Profile → Your car, so a click on "Colour" lands on the colour.
const ChangeCarTypeModal = ({ onClose, currentUserId, userData, addNotification, onCarDetailsUpdated, focusField }) => {
  const fieldRefs = { size: useRef(null), plate: useRef(null), color: useRef(null) };
  useEffect(() => {
    const el = fieldRefs[focusField]?.current;
    if (el) { el.focus(); if (el.select && focusField !== 'size') el.select(); }
    // Once, on opening: refocusing on every render would fight the person typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 2026-10-16: both were useState('') and neither was seeded, which is a data-loss bug rather than
  // an inconvenience. The endpoint builds COALESCE($n, column), and COALESCE returns the first
  // NON-NULL — an empty string is not null. So opening this dialog to change the TYPE and leaving
  // the colour box as it opened, blank, wrote '' over the stored colour. Seeded, and blanks are
  // omitted from the request below, so a field left alone is left alone.
  // Seeded as a size: a profile cached before the 2026-10-28 migration can still say "sedan".
  const [carType, setCarType] = useState(normalizeCarSize(userData?.car_type) || '');
  const [carColor, setCarColor] = useState(userData?.car_color || '');
  // 2026-10-16: the plate was editable on the phone and not here, though the server has always
  // accepted it on this same endpoint — PUT /api/users/:id/car-details takes plate_number and
  // COALESCEs it, so the field was simply never sent. Seeded from the current value rather than
  // blank: this is a correction, not a re-entry, and COALESCE would read an empty string as a real
  // one and wipe it.
  const [plateNumber, setPlateNumber] = useState(userData?.plate_number || '');
  // Opt-out default (true) matches the users.share_plate_number DB default — a spot owner's plate
  // is already hidden from anyone without an accepted-spot relationship regardless of this flag;
  // it only controls whether an accepted requester specifically sees it.
  const [sharePlateNumber, setSharePlateNumber] = useState(userData ? userData.share_plate_number !== false : true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      addNotification("You must be logged in to change car type.", 'default');
      return;
    }

    try {
      const response = await fetch(`/api/users/${currentUserId}/car-details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        // plate_number omitted when blank rather than sent empty: the endpoint COALESCEs, so an
        // empty string is a value and would erase the plate instead of leaving it alone.
        // Blank means "unchanged", not "erase". See the state above for why that distinction is
        // load-bearing against a COALESCE.
        body: JSON.stringify({
          share_plate_number: sharePlateNumber,
          ...(carType ? { car_type: carType } : {}),
          ...(carColor.trim() ? { car_color: carColor.trim() } : {}),
          ...(plateNumber.trim() ? { plate_number: plateNumber.trim() } : {}),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token); // Store the new token
        addNotification("Car type and color updated successfully!", 'green');
        onCarDetailsUpdated(); // Trigger update in App.js
        onClose();
      } else {
        const errorData = await response.json();
        addNotification(`Failed to update car details: ${errorData.message}`, 'default');
      }
    } catch (error) {
      console.error('Error updating car details:', error);
      addNotification('An error occurred while updating car details.', 'default');
    }
  };

  return (
    <div className="change-car-type-modal-overlay">
      <div className="change-car-type-modal-content">
        <div className="change-car-type-modal-header">
          <h2>Your car</h2>
          <button className="change-car-type-modal-close-button" onClick={onClose}>&times;</button>
        </div>
        <div className="change-car-type-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label className="field-label" htmlFor="car-type">Car size</label>
              <select
                ref={fieldRefs.size}
                className="control"
                id="car-type"
                value={carType}
                onChange={(e) => setCarType(e.target.value)}
                required
              >
                <CarSizeOptions />
              </select>
            </div>
            <div className="field">
              <label className="field-label" htmlFor="plate-number">Plate number</label>
              <input
                className="control"
                type="text"
                ref={fieldRefs.plate}
                id="plate-number"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
                placeholder="e.g., ABC 1234"
                autoCapitalize="characters"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="car-color">Colour</label>
              <input
                className="control"
                type="text"
                ref={fieldRefs.color}
                id="car-color"
                value={carColor}
                onChange={(e) => setCarColor(e.target.value)}
                placeholder="e.g., Red, Blue, Black"
              />
            </div>
            <div className="field">
              <label className="toggle-label">
                <span>
                  Share plate number
                  <span className="toggle-description">Once a handover is agreed, the other driver sees your full plate. Off, they see only its last 2 characters. Your car colour is shown either way.</span>
                </span>
                <span className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={sharePlateNumber}
                    onChange={(e) => setSharePlateNumber(e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </span>
              </label>
            </div>
            <div className="btn-row">
              <button type="submit" className="btn btn--primary">Update</button>
              <button type="button" className="btn btn--secondary" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangeCarTypeModal;